import { Box, Collapse, createStyles, Group, Modal, rem, Stack } from '@mantine/core';
import { useForm } from '@mantine/form';
import { IconArrowRight, IconChevronDown, IconChevronUp } from '@tabler/icons-react';
import { nanoid } from 'nanoid';
import { useCallback, useEffect, useRef, useState } from 'react';

import Button from 'components/Button';
import createValidator from 'components/forms/validators/create-validator';
import required from 'components/forms/validators/rules/rule-required';
import AutomationConditionsFields from 'pages/revisions-module/template-editor/editors/pipeline/configurator/AutomationConditionsFields';
import AutomationSubForm from 'pages/revisions-module/template-editor/editors/pipeline/configurator/AutomationSubForm';
import AutomationTypeSelect from 'pages/revisions-module/template-editor/editors/pipeline/configurator/AutomationTypeSelect';
import RevisionStatusSelect from 'pages/revisions-module/template-editor/editors/pipeline/configurator/RevisionStatusSelect';
import { IAutomation, IAutomationType } from 'pages/revisions-module/template-editor/editors/pipeline/types';

const useStyles = createStyles((theme) => ({
  wrapper: {
    borderTop: `${rem(1)} solid ${theme.colors.gray[1]}`,
  },
  section: {
    borderBottom: `${rem(1)} solid ${theme.colors.gray[1]}`,
  },
}));

/**
 * Shared clickable header styling for collapsible configurator sections.
 */
const clickableSectionHeaderSx = (theme: { colors: Record<string, string[]> }) => ({
  cursor: 'pointer',
  color: theme.colors.gray[7],
});

/**
 * Detects whether the automation has any shared execution conditions configured.
 */
const hasAutomationConditions = (automation?: Partial<IAutomation>) =>
  Boolean(automation?.runIfTrue) || (automation?.customerOrganizationIds?.length ?? 0) > 0;

/**
 * Parameters of the AutomationConfigurator component.
 */
export interface AutomationConfiguratorProps {
  opened: boolean;
  primaryButtonLabel?: string;
  readOnly?: Partial<Record<keyof IAutomation, boolean>>;
  initialValues?: IAutomation;
  automationTypes?: Partial<Record<IAutomationType, boolean>>;
  onClose: () => void;
  onSubmit: (spec: IAutomation) => void;
}

/**
 * Used to create and edit automations in a pipeline.
 */
export default function AutomationConfigurator({
  opened,
  primaryButtonLabel = 'Vytvoriť proces',
  readOnly = {},
  initialValues,
  automationTypes,
  onClose,
  onSubmit,
}: AutomationConfiguratorProps) {
  const { classes } = useStyles();
  const [conditionsOpened, setConditionsOpened] = useState(false);
  const [subFormOpened, setSubFormOpened] = useState(true);
  const wasOpenedRef = useRef(false);

  const form = useForm<IAutomation>({
    initialValues,
    validate: (values) => ({
      statusSource:
        values.statusSource === values.statusTarget
          ? 'Zdrojový a cieľový stav nemôžu byť rovnaké'
          : !values.statusSource
          ? 'Pole je povinné'
          : null,
      statusTarget:
        values.statusTarget === values.statusSource
          ? 'Zdrojový a cieľový stav nemôžu byť rovnaké'
          : !values.statusTarget
          ? 'Pole je povinné'
          : null,
      type: createValidator([required])(values.type),
      'fieldMappings.departmentName':
        values.type === 'create-child-department' && !values.fieldMappings?.departmentName ? 'Pole je povinné' : null,
    }),
  });

  const submit = useCallback(
    (spec: IAutomation) => {
      if (spec.id === undefined) {
        spec.id = nanoid();
      }

      onSubmit(spec);
      onClose();
    },
    [onClose, onSubmit]
  );

  useEffect(() => {
    if (opened && !wasOpenedRef.current) {
      form.reset();
      setSubFormOpened(true);
      setConditionsOpened(hasAutomationConditions(initialValues));

      if (initialValues) {
        form.setValues(initialValues);
      }
    }

    wasOpenedRef.current = opened;
  }, [form, initialValues, opened]);

  useEffect(() => {
    if (form.values.type) {
      setSubFormOpened(true);
      if (form.values.type === 'create-child-department' && !form.values.fieldMappings) {
        form.setValues({ fieldMappings: { departmentName: '' } });
      }
    }
  }, [form.values.type]);

  const isDirty = form.isDirty();

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title="Konfigurácia procesu"
      size="xl"
      closeOnClickOutside={!isDirty}
      closeOnEscape={!isDirty}
    >
      <form onSubmit={form.onSubmit(submit)}>
        <Stack spacing={16} pt={16} className={classes.wrapper}>
          <Stack spacing={0}>
            <Stack spacing={12} pb={16} className={classes.section}>
              <Group noWrap align="flex-end" spacing={12}>
                <Box sx={{ flex: 1 }}>
                  <RevisionStatusSelect label="Počiatočný stav" size="lg" {...form.getInputProps('statusSource')} />
                </Box>
                <Box pb={12} c="gray.6">
                  <IconArrowRight size={20} />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <RevisionStatusSelect
                    label="Cieľový stav"
                    size="lg"
                    omitValue={form.values.statusSource}
                    {...form.getInputProps('statusTarget')}
                  />
                </Box>
              </Group>
            </Stack>
            <Stack spacing={12} py={16} className={classes.section}>
              <AutomationTypeSelect
                readOnly={readOnly.type}
                automationTypes={automationTypes}
                {...form.getInputProps('type')}
              />
              {form.values.type && (
                <Stack spacing={12}>
                  <Group
                    position="apart"
                    noWrap
                    onClick={() => setSubFormOpened((openedState) => !openedState)}
                    sx={clickableSectionHeaderSx}
                  >
                    <Box>
                      <Box fz="sm" fw={500} c="gray.9">
                        Nastavenia procesu
                      </Box>
                      <Box fz="xs" c="gray.6">
                        Podrobnosti zvoleného typu procesu.
                      </Box>
                    </Box>
                    <Box>{subFormOpened ? <IconChevronUp size={18} /> : <IconChevronDown size={18} />}</Box>
                  </Group>
                  <Collapse in={subFormOpened}>
                    <Stack spacing={12} bg="red.0" p={12} sx={{ borderRadius: rem(4), ':empty': { display: 'none' } }}>
                      <AutomationSubForm type={form.values.type} form={form} />
                    </Stack>
                  </Collapse>
                </Stack>
              )}
            </Stack>
            {form.values.type && (
              <Stack spacing={12} py={16} className={classes.section}>
                <Group
                  position="apart"
                  noWrap
                  onClick={() => setConditionsOpened((openedState) => !openedState)}
                  sx={clickableSectionHeaderSx}
                >
                  <Box>
                    <Box fz="sm" fw={500} c="gray.9">
                      Podmienky spustenia
                    </Box>
                    <Box fz="xs" c="gray.6">
                      Všetky zapnuté podmienky musia byť splnené.
                    </Box>
                  </Box>
                  <Box>{conditionsOpened ? <IconChevronUp size={18} /> : <IconChevronDown size={18} />}</Box>
                </Group>
                <Collapse in={conditionsOpened}>
                  <Stack spacing={12} bg="red.0" p={12} sx={{ borderRadius: rem(4), ':empty': { display: 'none' } }}>
                    <AutomationConditionsFields form={form} />
                  </Stack>
                </Collapse>
              </Stack>
            )}
          </Stack>
          <Group position="right">
            <Button type="button" variant="subtle-gray" size="md" onClick={onClose}>
              Zrušiť
            </Button>
            <Button type="submit" variant="primary" size="md">
              {primaryButtonLabel}
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}
